import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useOrganizations } from '../hooks/api/useOrganizations';

export function OrganizationsPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const { data: organizations, isLoading, error } = useOrganizations(search);

  const sorted = useMemo(() => {
    if (!Array.isArray(organizations)) return [];
    return [...organizations].sort((a, b) => a.name.localeCompare(b.name));
  }, [organizations]);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t('organizations.title')}
      </Typography>

      <TextField
        size="small"
        label={t('organizations.search')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        fullWidth
      />

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">{error.message || t('common.error')}</Alert>}

      {!isLoading && !error && (
        <Paper>
          {sorted.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">{t('organizations.empty')}</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 560 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('organizations.name')}</TableCell>
                    <TableCell>{t('organizations.nickname')}</TableCell>
                    <TableCell>{t('organizations.plan')}</TableCell>
                    <TableCell>{t('organizations.status')}</TableCell>
                    <TableCell>{t('organizations.members')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorted.map((org) => (
                    <TableRow key={org.uuid} hover>
                      <TableCell>
                        <Link component={RouterLink} to={`/organizations/${org.uuid}`}>
                          {org.name}
                        </Link>
                      </TableCell>
                      <TableCell>{org.nickname}</TableCell>
                      <TableCell>{org.subscription?.planSlug ?? '—'}</TableCell>
                      <TableCell>{org.subscription?.status ?? org.subscriptionStatusCache ?? '—'}</TableCell>
                      <TableCell>{org.memberCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Box>
  );
}
